import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';

interface AttendanceRecord {
  _id: string;
  attendanceDate: string;
  status: string;
  className: string;
  remarks: string;
  staff: { _id: string; name: string };
}

const StudentAttendance: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isParentUser = Boolean(user?.parentName || user?.parentPhone);
  const backRoute = isParentUser ? '/parent/dashboard' : '/school-work';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [attendanceStats, setAttendanceStats] = useState<any | null>(null);
  const [attendanceStatsLoading, setAttendanceStatsLoading] = useState(false);
  const [attendanceStatsError, setAttendanceStatsError] = useState<string | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = Boolean(
    isHighSchoolEducation(user?.educationLevel) &&
    (user?.school || user?.schoolName || user?.schoolEmail)
  );

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/school-work/attendance');
        setAttendance(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load attendance');
      } finally {
        setLoading(false);
      }
    };
    
    if (hasSchoolWorkAccess) {
      fetchAttendance();
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  useEffect(() => {
    if (!attendance.length) {
      setAttendanceStats(null);
      return;
    }
    
    setAttendanceStatsLoading(true);
    setAttendanceStatsError(null);
    
    try {
      let presentDays = 0;
      let lateDays = 0;
      let excusedDays = 0;
      let absentDays = 0;
      const totalDays = attendance.length;

      attendance.forEach(record => {
        if (record.status === 'present') presentDays++;
        else if (record.status === 'late') lateDays++;
        else if (record.status === 'excused') excusedDays++;
        else if (record.status === 'absent') absentDays++;
      });

      const weightedDays = (presentDays * 1.0) + (excusedDays * 0.75) + (lateDays * 0.5);
      const percentage = totalDays > 0 ? (weightedDays / totalDays * 100) : 0;

      setAttendanceStats({
        presentDays,
        lateDays,
        excusedDays,
        absentDays,
        totalDays,
        attendancePercentage: Number(percentage.toFixed(2))
      });
    } catch (err) {
      setAttendanceStatsError('Unable to calculate attendance statistics');
    } finally {
      setAttendanceStatsLoading(false);
    }
  }, [attendance]);

  if (!hasSchoolWorkAccess && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          School Work is available only to high school students...
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Attendance</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(backRoute)}>
          Back to Dashboard
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Your attendance history</Typography>
          
          {attendanceStatsLoading ? (
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Loading attendance percentage...</Typography>
          ) : attendanceStatsError ? (
            <Typography variant="subtitle1" sx={{ mb: 2 }} color="error">{attendanceStatsError}</Typography>
          ) : attendanceStats ? (
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Attendance Percentage: <b>{attendanceStats.attendancePercentage?.toFixed(2) ?? '0.00'}%</b>
            </Typography>
          ) : null}

          {attendance.length === 0 ? (
            <Alert severity="info">No attendance records available yet.</Alert>
          ) : !selectedTeacherId ? (
            <Grid container spacing={3}>
              {Object.values(
                attendance.reduce((acc, record) => {
                  const teacher = record.staff;
                  if (teacher) {
                    if (!acc[teacher._id]) {
                      acc[teacher._id] = { ...teacher, count: 0 };
                    }
                    acc[teacher._id].count += 1;
                  } else {
                    if (!acc['general']) {
                      acc['general'] = { _id: 'general', name: 'General/Unknown Teacher', count: 0 };
                    }
                    acc['general'].count += 1;
                  }
                  return acc;
                }, {} as Record<string, { _id: string; name: string; count: number }>)
              ).map((teacher: any) => (
                <Grid item xs={12} sm={6} md={4} key={teacher._id}>
                  <Card 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
                    onClick={() => setSelectedTeacherId(teacher._id)}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>{teacher.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {teacher.count} Record{teacher.count !== 1 ? 's' : ''} Taken
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              <Button sx={{ mb: 2 }} onClick={() => setSelectedTeacherId(null)} startIcon={<ArrowBack />}>
                Back to Teachers
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Teacher</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendance
                      .filter(a => (a.staff?._id || 'general') === selectedTeacherId)
                      .map((record) => (
                      <TableRow key={record._id}>
                        <TableCell>{new Date(record.attendanceDate).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.status}
                            color={record.status === 'present' ? 'success' : record.status === 'absent' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{record.className}</TableCell>
                        <TableCell>{record.staff?.name || 'Teacher'}</TableCell>
                        <TableCell>{record.remarks || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentAttendance;
